import React, { useEffect, useState } from "react";
import ThemedDropdown from "components/common/DropDown/ThemedDropdown";
import { classNames } from "Utils/utils";
import {
  CARE_WATCH_STATUSES,
  CARE_WATCH_VIEWS,
  CATEGORY_OPTIONS,
  DOC_STATUS_OPTIONS,
  ISSUE_OPTIONS,
  LIVE_STATES,
  LIVE_STATUS_OPTIONS,
  RISK_OPTIONS,
  STATUS_OPTIONS,
  isCareWatchLiveView,
} from "../utils/constants";
import CareWatchFilterDateRange from "./CareWatchFilterDateRange";
import { IconSearch } from "./CareWatchIcons";

const SEARCH_DEBOUNCE_MS = 400;
const SEARCH_ID = "care-watch-filter-search";

const CareWatchFilterBar = ({
  filters,
  onChange,
  onChangeMany,
  onClear,
  hasActiveFilters = false,
}) => {
  const isLiveView = isCareWatchLiveView(filters.view);
  const isDocView = filters.view === CARE_WATCH_VIEWS.DOCUMENTATION;
  const [search, setSearch] = useState(filters.search || "");
  const canClear = hasActiveFilters || Boolean(search.trim());
  const fieldCount = isLiveView ? 1 : isDocView ? 3 : 4;
  const statusValue =
    isDocView && filters.status === CARE_WATCH_STATUSES.PROMOTED
      ? CARE_WATCH_STATUSES.NEEDS_FOLLOW_UP
      : filters.status;

  useEffect(() => {
    setSearch(filters.search || "");
  }, [filters.search]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if ((filters.search || "") !== search) {
        onChange("search", search);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [search, filters.search, onChange]);

  return (
    <div className="care-watch-filters">
      <div className="care-watch-filters-row">
        <div className="care-watch-filters-search">
          <label className="care-watch-filters-label" htmlFor={SEARCH_ID}>
            Search
          </label>
          <div
            className={classNames(
              "themed-dropdown-trigger care-watch-filters-search-field",
              search.trim() && "is-applied"
            )}
          >
            <IconSearch size={15} className="shrink-0 text-[#94A3B8]" />
            <input
              id={SEARCH_ID}
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search participant or worker…"
            />
          </div>
        </div>

        <div className="care-watch-filters-fields" data-count={fieldCount}>
          {!isLiveView && (
            <CareWatchFilterDateRange
              className="care-watch-filter-ctrl care-watch-filter-ctrl--date"
              fromDate={filters.fromDate}
              toDate={filters.toDate}
              onChange={({ fromDate, toDate }) => {
                if (onChangeMany) {
                  onChangeMany({ fromDate, toDate });
                  return;
                }
                onChange("fromDate", fromDate);
                onChange("toDate", toDate);
              }}
            />
          )}
          {isLiveView ? (
            <ThemedDropdown
              label="Status"
              value={
                filters.liveState === LIVE_STATES.ON_FLOOR
                  ? LIVE_STATES.ALL
                  : filters.liveState || LIVE_STATES.ALL
              }
              options={LIVE_STATUS_OPTIONS}
              onChange={(value) => onChange("liveState", value)}
              widthClass="care-watch-filter-ctrl care-watch-filter-ctrl--status"
              applied={
                Boolean(filters.liveState) &&
                filters.liveState !== LIVE_STATES.ALL &&
                filters.liveState !== LIVE_STATES.ON_FLOOR
              }
            />
          ) : (
            <>
              {isDocView ? (
                <ThemedDropdown
                  label="Issue"
                  value={filters.issue || "all"}
                  options={ISSUE_OPTIONS}
                  onChange={(value) => onChange("issue", value)}
                  widthClass="care-watch-filter-ctrl care-watch-filter-ctrl--issue"
                  applied={Boolean(filters.issue) && filters.issue !== "all"}
                />
              ) : (
                <ThemedDropdown
                  multiple
                  label="Category"
                  value={filters.category}
                  options={CATEGORY_OPTIONS}
                  allLabel="All categories"
                  onChange={(value) => onChange("category", value)}
                  widthClass="care-watch-filter-ctrl care-watch-filter-ctrl--category"
                  applied={
                    Array.isArray(filters.category)
                      ? filters.category.length > 0
                      : Boolean(filters.category) && filters.category !== "all"
                  }
                />
              )}
              {!isDocView && (
                <ThemedDropdown
                  label="Risk"
                  value={filters.risk || "all"}
                  options={RISK_OPTIONS}
                  onChange={(value) => onChange("risk", value)}
                  widthClass="care-watch-filter-ctrl care-watch-filter-ctrl--risk"
                  applied={Boolean(filters.risk) && filters.risk !== "all"}
                />
              )}
              <ThemedDropdown
                label="Status"
                value={statusValue}
                options={isDocView ? DOC_STATUS_OPTIONS : STATUS_OPTIONS}
                onChange={(value) => onChange("status", value)}
                widthClass="care-watch-filter-ctrl care-watch-filter-ctrl--status"
                applied={statusValue !== "needs_follow_up"}
              />
            </>
          )}
        </div>

        <button
          type="button"
          onClick={() => {
            setSearch("");
            onClear();
          }}
          disabled={!canClear}
          className={classNames(
            "care-watch-filters-clear",
            canClear ? "is-active" : "is-disabled"
          )}
        >
          Clear filters
        </button>
      </div>
    </div>
  );
};

export default CareWatchFilterBar;
